import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Dayjs } from "dayjs";
import { CalendarOutlined } from "@ant-design/icons";
import RangeCalendar, { RangeValue } from "./RangeCalendar";
import styles from "./styles.module.scss";

export type DatePeriod = "week" | "month" | "quarter";

const PERIODS: { value: DatePeriod; label: string }[] = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

interface DateRangeFilterProps {
  period: DatePeriod | null;
  /** null when the active period is clicked again (no date filter). */
  onPeriodChange: (period: DatePeriod | null) => void;
  range: [Dayjs, Dayjs] | null;
  /** null on Clear. */
  onRangeChange: (range: [Dayjs, Dayjs] | null) => void;
}

const formatRange = ([start, end]: [Dayjs, Dayjs]) =>
  start.isSame(end, "month")
    ? `${start.format("MMM D")} - ${end.format("D")}`
    : `${start.format("MMM D")} - ${end.format("MMM D")}`;

/**
 * The client's date filter (Balance / payments): a segmented Week / Month /
 * Quarter control whose last segment opens the two-month range calendar.
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  period,
  onPeriodChange,
  range,
  onRangeChange,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<RangeValue>(range ?? [null, null]);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // The calendar starts from the applied range each time it opens.
  const toggle = () => {
    if (!open) setDraft(range ?? [null, null]);
    setOpen(!open);
  };

  const apply = () => {
    if (draft[0] && draft[1]) {
      onRangeChange([draft[0], draft[1]]);
      setOpen(false);
    }
  };

  const clear = () => {
    setDraft([null, null]);
    onRangeChange(null);
    setOpen(false);
  };

  return (
    <div className={styles.filter} ref={rootRef}>
      {PERIODS.map((item) => (
        <button
          key={item.value}
          type="button"
          className={cn(styles.segment, {
            [styles.active]: period === item.value,
          })}
          aria-pressed={period === item.value}
          onClick={() =>
            onPeriodChange(period === item.value ? null : item.value)
          }
        >
          {item.label}
        </button>
      ))}
      <button
        type="button"
        className={cn(styles.segment, styles.rangeSegment, {
          [styles.active]: !!range,
        })}
        aria-expanded={open}
        onClick={toggle}
      >
        <CalendarOutlined className={styles.calendarIcon} />
        {range ? formatRange(range) : "Custom"}
      </button>
      {open && (
        <div className={styles.popover}>
          <RangeCalendar
            value={draft}
            onChange={setDraft}
            onApply={apply}
            onClear={clear}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
